<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import {
  formatWithoutRounding,
  formatPrice,
} from "../../mixins/productPrice.mixin";
import { formatCurrency } from "../../utils/currencyHelpers";
import autoAnimate from "@formkit/auto-animate";
import ShoppingCartError from "./shoppingCartError.vue";
import PaymentProviders from "./paymentProviders.vue";
import { CheckoutStages } from "../../services/models/checkout.models";
import { useShoppingCartStore } from "./stores/shoppingCartStore";
import CheckoutService from "../../services/checkout.service";

const props = defineProps<{
  showCheckoutLink?: boolean;
  error?: string;
}>();

const shoppingCartStore = useShoppingCartStore();
const root = ref();
const totalList = ref();

const error = computed(() => {
  return props.error;
});

/**
 * Handles the action of navigating to the checkout stage.
 *
 * @function handleGoToCheckout
 * @returns {void}
 */
const handleGoToCheckout = () => {
  CheckoutService.goToCheckoutStage(CheckoutStages.Verification);
};

onMounted(() => {
  autoAnimate(root.value);
  autoAnimate(totalList.value);
});
</script>

<template>
  <div ref="root" class="checkout-summary">
    <h2 class="checkout-summary__heading">{{ $t("summary") }}</h2>
    <ShoppingCartError :error="error" type="global" />
    <div>
      <ul
        v-if="shoppingCartStore.cart?.lineItems"
        class="checkout-summary__group"
      >
        <li
          v-for="lineItem in shoppingCartStore.cart.lineItems"
          :key="lineItem.code"
          class="checkout-summary__entry"
        >
          <div class="checkout-summary__product">
            <span class="checkout-summary__product-name">
              {{ lineItem.name }} ({{ lineItem.quantity }} stk)
            </span>
          </div>
          <div class="checkout-summary__price">
            <span class="checkout-summary__price"
              >{{ formatCurrency(lineItem.listPrice.currency) }}
              {{
                formatWithoutRounding(
                  lineItem.quantity * lineItem.discountedPrice.amount,
                )
              }}
            </span>
          </div>
        </li>
      </ul>
      <div ref="totalList" class="checkout-summary__group">
        <div
          v-if="shoppingCartStore.cart?.discountTotal?.amount"
          class="checkout-summary__entry checkout-summary__entry"
        >
          <div>{{ $t("discountTotal") }}</div>
          <div>
            <span class="checkout-summary__discount"
              >-
              {{
                shoppingCartStore.cart.discountTotal &&
                formatCurrency(shoppingCartStore.cart.discountTotal.currency)
              }}
              {{
                formatWithoutRounding(
                  shoppingCartStore.cart.discountTotal.amount,
                )
              }}</span
            >
          </div>
        </div>
        <div
          v-if="shoppingCartStore.cart?.total?.amount"
          class="checkout-summary__entry checkout-summary__entry"
        >
          <div>{{ $t("shipping") }}</div>
          <div>
            <span>{{ shoppingCartStore.shippingCostFormatted }}</span>
          </div>
        </div>
        <div
          v-if="shoppingCartStore.cart && !shoppingCartStore.freeShipping"
          class="checkout-summary__entry checkout-summary__entry--gray"
        >
          <div class="checkout-summary__shipping-tip">
            {{ shoppingCartStore.shippingCallToActionText }}
          </div>
        </div>
      </div>
      <h4 class="checkout-summary__total">
        <span>{{ $t("total") }}: </span>
        <span
          >{{
            shoppingCartStore.cart?.total &&
            formatCurrency(shoppingCartStore.cart.total.currency)
          }}
          {{
            shoppingCartStore.cart?.total &&
            formatWithoutRounding(shoppingCartStore.totalAmount)
          }}</span
        >
      </h4>
      <div
        v-if="props.showCheckoutLink || shoppingCartStore.cart?.paymentMethods"
        class="checkout-summary__extra"
      >
        <button
          v-if="props.showCheckoutLink"
          class="btn btn--profile btn--smaller"
          @click="handleGoToCheckout"
        >
          <span>{{ $t("toCheckout") }}</span>
        </button>
        <div
          v-if="shoppingCartStore.cart?.paymentMethods"
          class="checkout-summary__extra-info"
        >
          <span>{{ $t("acceptedPaymentMethods") }}</span>
          <div class="layout-new-checkout__summary-fixed-payment-methods">
            <img
              v-for="(logo) in shoppingCartStore.paymentLogos"
              :key="logo.id"
              :src="logo.url"
              :alt="logo.alt"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
